import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Star, MessageCircle, AlertTriangle, TrendingUp } from 'lucide-react';

const fmt = v => v != null && !isNaN(v) ? Number(v).toLocaleString('en-IN') : '—';

export default function ExternalReviewsTab() {
  const { getHeaders, API } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [filterOutlet, setFilterOutlet] = useState('all');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/outlets`, { headers: getHeaders() })
      .then(res => setOutlets(res.data)).catch(() => {});
  }, [API, getHeaders]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/customer-experience`;
      if (filterOutlet !== 'all') url += `?outlet_id=${filterOutlet}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setRecords(res.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [API, getHeaders, filterOutlet]);

  useEffect(() => { load(); }, [load]);

  const outletMap = {};
  records.forEach(r => {
    if (!outletMap[r.outlet_id]) outletMap[r.outlet_id] = [];
    outletMap[r.outlet_id].push(r);
  });

  const outletName = id => outlets.find(o => o.id === id)?.name || id;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#5C6B64]">Weekly Google / Zomato / Swiggy reviews logged by managers</p>
        <Select value={filterOutlet} onValueChange={setFilterOutlet}>
          <SelectTrigger className="w-44 h-9 border-[#E2E8DF] text-sm" data-testid="cx-outlet-filter">
            <SelectValue placeholder="All Outlets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Outlets</SelectItem>
            {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
        </div>
      )}

      {!loading && records.length === 0 && (
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardContent className="py-10 text-center">
            <MessageCircle className="w-8 h-8 text-[#5C6B64]/30 mx-auto mb-2" />
            <p className="text-sm text-[#5C6B64]">No external review data submitted yet</p>
            <p className="text-xs text-[#5C6B64] mt-1">Managers submit weekly CX reports from their dashboard</p>
          </CardContent>
        </Card>
      )}

      {!loading && records.length > 0 && (
        <div className="space-y-4">
          {Object.entries(outletMap).map(([outletId, recs]) => {
            const sorted = [...recs].sort((a, b) => b.week_start.localeCompare(a.week_start));
            const latest = sorted[0];
            return (
              <Card key={outletId} className="border-[#E2E8DF] shadow-sm overflow-hidden" data-testid={`cx-card-${outletId}`}>
                <CardHeader className="pb-2 pt-3 px-4 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-semibold text-[#1A2421]">{outletName(outletId)}</CardTitle>
                  <Badge variant="outline" className="text-[10px] border-[#E2E8DF] text-[#5C6B64]">
                    {sorted.length} week{sorted.length !== 1 ? 's' : ''}
                  </Badge>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-1">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                      <p className="text-[10px] text-[#5C6B64] uppercase flex items-center justify-center gap-1">
                        <Star className="w-2.5 h-2.5" /> Avg Rating
                      </p>
                      <p className="text-sm font-bold text-[#1A2421]" data-testid={`cx-rating-${outletId}`}>
                        {latest.avg_rating != null ? Number(latest.avg_rating).toFixed(1) : '—'}
                      </p>
                    </div>
                    <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                      <p className="text-[10px] text-[#5C6B64] uppercase flex items-center justify-center gap-1">
                        <MessageCircle className="w-2.5 h-2.5" /> Reviews
                      </p>
                      <p className="text-sm font-bold text-[#1A2421]">{fmt(latest.total_reviews ?? latest.direct_reviews)}</p>
                    </div>
                    <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                      <p className="text-[10px] text-[#5C6B64] uppercase flex items-center justify-center gap-1">
                        <AlertTriangle className="w-2.5 h-2.5" /> Complaints
                      </p>
                      <p className="text-sm font-bold text-[#1A2421]">{fmt(latest.complaints ?? 0)}</p>
                    </div>
                    <div className="p-2 bg-[#F9F9F8] rounded-lg text-center">
                      <p className="text-[10px] text-[#5C6B64] uppercase flex items-center justify-center gap-1">
                        <TrendingUp className="w-2.5 h-2.5" /> NPS
                      </p>
                      <p className="text-sm font-bold text-[#1A2421]">{latest.nps != null ? Number(latest.nps).toFixed(0) : '—'}</p>
                    </div>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-[#E2E8DF]">
                    <table className="w-full text-sm text-left text-[#1A2421]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-[0.05em] text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                          <th className="px-3 py-2 font-medium">Week</th>
                          <th className="px-3 py-2 font-medium text-right">Rating</th>
                          <th className="px-3 py-2 font-medium text-right">Reviews</th>
                          <th className="px-3 py-2 font-medium text-right">Complaints</th>
                          <th className="px-3 py-2 font-medium text-right">NPS</th>
                          <th className="px-3 py-2 font-medium">Key Issues</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((r, i) => (
                          <tr key={r.id || i} className={`border-b border-[#E2E8DF] ${i % 2 === 0 ? '' : 'bg-[#F9F9F8]/50'}`}>
                            <td className="px-3 py-2 text-xs font-medium whitespace-nowrap">{r.week_start}</td>
                            <td className="px-3 py-2 text-right text-xs">
                              <span className={`font-medium ${(r.avg_rating || 0) >= 4 ? 'text-[#2D6A4F]' : (r.avg_rating || 0) >= 3 ? 'text-[#D4A017]' : 'text-[#C45A41]'}`}>
                                {r.avg_rating != null ? Number(r.avg_rating).toFixed(1) : '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right text-xs">{fmt(r.total_reviews ?? r.direct_reviews)}</td>
                            <td className="px-3 py-2 text-right text-xs">{fmt(r.complaints ?? 0)}</td>
                            <td className="px-3 py-2 text-right text-xs">{r.nps != null ? Number(r.nps).toFixed(0) : '—'}</td>
                            <td className="px-3 py-2 text-xs text-[#5C6B64] max-w-[200px] truncate">{r.key_issues || r.remarks || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
